import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import api from '../lib/api';
import { 
  CheckCircle2, Clock, AlertTriangle, Calendar, 
  Loader2, FolderKanban, ChevronRight, Sparkles,
  Brain, User, Users, Briefcase, RefreshCw, Lightbulb,
  Target, Zap, ChevronDown, ChevronUp, ToggleLeft, ToggleRight,
  CornerDownRight
} from 'lucide-react';
import { toast } from 'sonner';
import { format, isToday, isPast, parseISO } from 'date-fns';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';

const AllTasksDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { workspaces } = useWorkspace();
  
  const [allTasks, setAllTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiTips, setAiTips] = useState([]);
  const [aiPlan, setAiPlan] = useState(null);
  const [loadingTips, setLoadingTips] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [expandedWorkspaces, setExpandedWorkspaces] = useState({});
  const [viewMode, setViewMode] = useState('my_tasks'); // 'my_tasks' or 'all_tasks'

  const fetchAllTasks = useCallback(async () => {
    try {
      const response = await api.get('/tasks', { params: { limit: 500 } });
      const tasks = response.data || [];
      setAllTasks(tasks);
      setExpandedWorkspaces({});
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllTasks();
  }, [fetchAllTasks]);

  // Listen for task-created events from sidebar Add Task / Voice Input
  useEffect(() => {
    const handler = () => fetchAllTasks();
    window.addEventListener('task-created', handler);
    return () => window.removeEventListener('task-created', handler);
  }, [fetchAllTasks]);

  // Filtered tasks based on view mode
  // In "my_tasks" mode: include tasks assigned to user, unassigned tasks,
  // AND tasks where any subtask is assigned to the user
  const filteredTasks = useMemo(() => {
    if (viewMode === 'all_tasks') return allTasks;
    const uid = user?.user_id;
    return allTasks.filter(t =>
      t.assigned_to === uid ||
      !t.assigned_to ||
      t.assigned_to === '' ||
      (t.subtasks || []).some(st => st.assigned_to === uid)
    );
  }, [allTasks, viewMode, user?.user_id]);

  // Stats computed from filtered tasks
  // Subtasks assigned to the logged-in user count as tasks
  const todayStr = useMemo(() => format(new Date(), 'yyyy-MM-dd'), []);

  const stats = useMemo(() => {
    const uid = user?.user_id;

    let total = filteredTasks.length;
    let completed = filteredTasks.filter(t => t.completed).length;

    // Count subtasks explicitly assigned to this user
    filteredTasks.forEach(t => {
      const assignedSubs = (t.subtasks || []).filter(st => st.assigned_to === uid);
      total += assignedSubs.length;
      completed += assignedSubs.filter(st => st.completed).length;
    });

    const overdue = filteredTasks.filter(t =>
      !t.completed && t.due_date && t.due_date.length >= 10 && t.due_date < todayStr
    ).length;
    const today = filteredTasks.filter(t =>
      !t.completed && t.due_date && t.due_date.startsWith(todayStr)
    ).length;

    return { total, completed, overdue, today };
  }, [filteredTasks, user?.user_id, todayStr]);

  // Fetch AI productivity tips
  const fetchAiTips = async () => {
    setLoadingTips(true);
    try {
      const response = await api.get('/ai/daily-tips');
      setAiTips(response.data.tips || []);
    } catch (error) {
      const tips = [];
      const pendingTasks = filteredTasks.filter(t => !t.completed);
      const highPriorityCount = pendingTasks.filter(t => t.priority === 'high').length;
      if (highPriorityCount > 3) {
        tips.push({ icon: 'target', text: `You have ${highPriorityCount} high-priority tasks. Focus on completing 2-3 today.` });
      }
      if (stats.overdue > 0) {
        tips.push({ icon: 'alert', text: `${stats.overdue} overdue tasks need attention. Consider rescheduling or delegating.` });
      }
      if (pendingTasks.length > 20) {
        tips.push({ icon: 'lightbulb', text: 'Large task list detected. Try breaking tasks into smaller, manageable chunks.' });
      }
      tips.push({ icon: 'zap', text: 'Start your day with the most challenging task when your energy is highest.' });
      setAiTips(tips.slice(0, 3));
    } finally {
      setLoadingTips(false);
    }
  };

  // CORE AI Assistant
  const planMyDay = async () => {
    setLoadingPlan(true);
    try {
      const now = new Date();
      const response = await api.post('/ai/plan-day', {
        client_time: format(now, 'HH:mm'),
        client_date: format(now, 'yyyy-MM-dd')
      });
      setAiPlan(response.data);
    } catch (error) {
      setAiPlan({
        summary: "Could not generate plan. Focus on overdue and high-priority tasks first.",
        schedule: [],
        deferred: [],
        stats: { total: 0, overdue: 0, due_today: 0, high_priority: 0 }
      });
    } finally {
      setLoadingPlan(false);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const getWorkspaceIcon = (type) => {
    if (type === 'team') return <Users className="w-4 h-4" />;
    if (type === 'client') return <Briefcase className="w-4 h-4" />;
    return <User className="w-4 h-4" />;
  };

  // Get subtasks relevant to the current view
  const getRelevantSubtasks = (task) => {
    const subs = task.subtasks || [];
    if (viewMode === 'all_tasks') return subs;
    const uid = user?.user_id;
    return subs.filter(st => st.assigned_to === uid || !st.assigned_to || st.assigned_to === '');
  };

  // Inline subtask rows for dashboard task items
  const SubtaskRows = ({ task }) => {
    const subs = getRelevantSubtasks(task);
    if (subs.length === 0) return null;
    return (
      <div className="ml-6 mt-1 space-y-0.5">
        {subs.slice(0, 3).map(st => (
          <div key={st.subtask_id} className="flex items-center gap-2 text-xs text-muted-foreground">
            <CornerDownRight className="w-3 h-3 flex-shrink-0" />
            <span className={st.completed ? 'line-through' : 'text-foreground'}>{st.title}</span>
            {st.assigned_to_name && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300">
                {st.assigned_to_name}
              </span>
            )}
          </div>
        ))}
        {subs.length > 3 && (
          <span className="text-[10px] text-muted-foreground ml-5">+{subs.length - 3} more subtasks</span>
        )}
      </div>
    );
  };

  const formatTaskDate = (dateStr, timeStr) => {
    if (!dateStr) return null;
    try {
      const date = parseISO(dateStr);
      let formatted = format(date, 'MMM d');
      if (timeStr) formatted += ` at ${timeStr}`;
      return formatted;
    } catch {
      return dateStr;
    }
  };

  // Group tasks by workspace and project - only PENDING tasks from filteredTasks
  const groupedTasks = filteredTasks
    .filter(task => !task.completed)
    .reduce((acc, task) => {
      const wsKey = task.workspace_id || 'personal';
      const workspace = workspaces.find(w => w.workspace_id === task.workspace_id);
      if (!acc[wsKey]) {
        acc[wsKey] = {
          workspace_id: wsKey,
          workspace_name: task.workspace_name || workspace?.name || 'Personal',
          workspace_type: workspace?.workspace_type || 'personal',
          projects: {}
        };
      }
      const projKey = task.project_id || 'no_project';
      if (!acc[wsKey].projects[projKey]) {
        acc[wsKey].projects[projKey] = {
          project_id: projKey,
          project_name: task.project_name || 'No Project',
          project_color: task.project_color || '#4A9CC7',
          tasks: []
        };
      }
      acc[wsKey].projects[projKey].tasks.push(task);
      return acc;
    }, {});

  // Get today's tasks from filtered
  const todayTasks = filteredTasks.filter(t =>
    !t.completed && t.due_date && t.due_date.startsWith(todayStr)
  );

  // Get overdue tasks from filtered (due_date < today, exclude empty strings)
  const overdueTasks = filteredTasks.filter(t =>
    !t.completed && t.due_date && t.due_date.length >= 10 && t.due_date < todayStr
  );

  const toggleWorkspace = (wsKey) => {
    setExpandedWorkspaces(prev => ({ ...prev, [wsKey]: !prev[wsKey] }));
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-8 h-8 text-[#4A9CC7] animate-spin" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="h-full overflow-auto bg-background" data-testid="dashboard-page">
        <div className="max-w-6xl mx-auto p-4 md:p-6">
          {/* Header */}
          <div className="mb-6 md:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-foreground">
                {getGreeting()}, {user?.name?.split(' ')[0] || 'there'}!
              </h1>
              <p className="text-muted-foreground mt-1">
                {viewMode === 'my_tasks'
                  ? 'Your tasks & unassigned tasks across workspaces'
                  : "All team members' tasks across workspaces"}
              </p>
            </div>

            {/* View Mode Toggle */}
            <button
              onClick={() => setViewMode(prev => prev === 'my_tasks' ? 'all_tasks' : 'my_tasks')}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border bg-card hover:bg-muted transition-colors text-sm self-start sm:self-auto"
              data-testid="dashboard-view-toggle"
            >
              {viewMode === 'my_tasks' ? (
                <>
                  <ToggleLeft className="w-5 h-5 text-[#4A9CC7]" />
                  <span className="text-foreground font-medium">My Tasks</span>
                </>
              ) : (
                <>
                  <ToggleRight className="w-5 h-5 text-purple-500" />
                  <span className="text-foreground font-medium">All Users</span>
                </>
              )}
            </button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="dark:bg-slate-800 dark:border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                    <FolderKanban className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground" data-testid="stat-total">{stats.total}</p>
                    <p className="text-sm text-muted-foreground">Total Tasks</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="dark:bg-slate-800 dark:border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground" data-testid="stat-completed">{stats.completed}</p>
                    <p className="text-sm text-muted-foreground">Completed</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="dark:bg-slate-800 dark:border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-yellow-100 dark:bg-yellow-900 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground" data-testid="stat-today">{stats.today}</p>
                    <p className="text-sm text-muted-foreground">Due Today</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="dark:bg-slate-800 dark:border-slate-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground" data-testid="stat-overdue">{stats.overdue}</p>
                    <p className="text-sm text-muted-foreground">Overdue</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* AI Section */}
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {/* AI Productivity Tips */}
            <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-950 dark:to-indigo-950 border-purple-100 dark:border-purple-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between text-foreground">
                  <span className="flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                    AI Productivity Tips
                  </span>
                  <Button 
                    variant="ghost" size="sm" onClick={fetchAiTips}
                    disabled={loadingTips} className="h-8"
                    data-testid="refresh-tips-btn"
                  >
                    {loadingTips ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {aiTips.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-sm text-muted-foreground mb-2">Get personalized productivity tips</p>
                    <Button 
                      size="sm" onClick={fetchAiTips} disabled={loadingTips}
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="get-tips-btn"
                    >
                      {loadingTips ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                      Get Tips
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {aiTips.map((tip, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-2 bg-white/60 dark:bg-slate-800/60 rounded-lg">
                        <div className="w-6 h-6 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center flex-shrink-0 mt-0.5">
                          {tip.icon === 'target' && <Target className="w-3 h-3 text-purple-600 dark:text-purple-400" />}
                          {tip.icon === 'alert' && <AlertTriangle className="w-3 h-3 text-purple-600 dark:text-purple-400" />}
                          {tip.icon === 'lightbulb' && <Lightbulb className="w-3 h-3 text-purple-600 dark:text-purple-400" />}
                          {tip.icon === 'zap' && <Zap className="w-3 h-3 text-purple-600 dark:text-purple-400" />}
                          {!tip.icon && <Sparkles className="w-3 h-3 text-purple-600 dark:text-purple-400" />}
                        </div>
                        <p className="text-sm text-foreground">{tip.text}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* CORE AI Assistant - Trigger */}
            <Card className="bg-gradient-to-br from-[#4A9CC7]/10 to-blue-50 dark:from-[#4A9CC7]/20 dark:to-blue-950 border-[#4A9CC7]/20 dark:border-[#4A9CC7]/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between text-foreground">
                  <span className="flex items-center gap-2">
                    <Brain className="w-5 h-5 text-[#4A9CC7]" />
                    CORE AI Assistant
                  </span>
                  {aiPlan && (
                    <Button variant="ghost" size="sm" onClick={() => setAiPlan(null)} className="h-8 text-xs" data-testid="replan-btn">
                      <RefreshCw className="w-3 h-3 mr-1" /> Re-plan
                    </Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {!aiPlan ? (
                  <div className="text-center py-4">
                    <p className="text-sm text-muted-foreground mb-2">Let CORE AI build a time-blocked schedule based on your assigned tasks</p>
                    <Button 
                      size="sm" onClick={planMyDay} disabled={loadingPlan}
                      className="bg-[#4A9CC7] hover:bg-[#3d8ab5]"
                      data-testid="plan-day-btn"
                    >
                      {loadingPlan ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Brain className="w-4 h-4 mr-2" />}
                      Plan My Day
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Summary */}
                    <p className="text-sm text-foreground bg-white/60 dark:bg-slate-800/60 p-2.5 rounded-lg leading-relaxed" data-testid="plan-summary">
                      {aiPlan.summary}
                    </p>

                    {/* Stats pills */}
                    {aiPlan.stats && (
                      <div className="flex flex-wrap gap-2">
                        {aiPlan.stats.overdue > 0 && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 font-medium">
                            {aiPlan.stats.overdue} overdue
                          </span>
                        )}
                        {aiPlan.stats.due_today > 0 && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 font-medium">
                            {aiPlan.stats.due_today} due today
                          </span>
                        )}
                        {aiPlan.stats.high_priority > 0 && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300 font-medium">
                            {aiPlan.stats.high_priority} high priority
                          </span>
                        )}
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-medium">
                          {aiPlan.stats.total} total
                        </span>
                      </div>
                    )}

                    {/* Schedule timeline */}
                    {aiPlan.schedule?.length > 0 && (
                      <div className="space-y-1.5" data-testid="plan-schedule">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Schedule</p>
                        {aiPlan.schedule.map((item, idx) => (
                          <div key={idx} className="flex items-start gap-2.5 p-2 bg-white/60 dark:bg-slate-800/60 rounded-lg">
                            <div className="flex-shrink-0 w-[90px]">
                              <span className="text-[11px] font-mono font-medium text-[#4A9CC7] dark:text-[#5fb3dc]">
                                {item.time_block}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                {item.is_subtask && <CornerDownRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />}
                                <span className="text-sm text-foreground truncate">{item.title}</span>
                              </div>
                              <p className="text-[10px] text-muted-foreground mt-0.5">{item.reason}</p>
                            </div>
                            <Badge className={`text-[9px] flex-shrink-0 ${
                              item.priority === 'urgent' ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300' :
                              item.priority === 'high' ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300' :
                              item.priority === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300' :
                              'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                            }`}>
                              {item.priority}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Deferred tasks */}
                    {aiPlan.deferred?.length > 0 && (
                      <div data-testid="plan-deferred">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Deferred</p>
                        {aiPlan.deferred.map((item, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-xs p-1.5 text-muted-foreground">
                            <Clock className="w-3 h-3 mt-0.5 flex-shrink-0" />
                            <div>
                              <span className="text-foreground">{item.title}</span>
                              <span className="ml-1.5 text-[10px]">- {item.reason}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Today's Tasks & Overdue */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            {/* Today's Tasks */}
            <Card className="dark:bg-slate-800 dark:border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-foreground">
                  <Calendar className="w-5 h-5 text-[#4A9CC7]" />
                  Today's Tasks
                  {todayTasks.length > 0 && (
                    <Badge className="ml-2 bg-[#4A9CC7]">{todayTasks.length}</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {todayTasks.length === 0 ? (
                  <p className="text-muted-foreground text-sm py-4 text-center">No tasks due today</p>
                ) : (
                  <div className="space-y-2">
                    {todayTasks.slice(0, 5).map(task => (
                      <div key={task.task_id} className="p-2 rounded-lg hover:bg-muted">
                        <div className="flex items-start gap-3">
                          <div className={`w-4 h-4 mt-0.5 rounded-full border-2 flex-shrink-0 ${task.completed ? 'bg-[#4A9CC7] border-[#4A9CC7]' : 'border-gray-300 dark:border-gray-600'}`} />
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm text-foreground ${task.completed ? 'line-through text-muted-foreground' : ''}`}>
                              {task.title}
                            </p>
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                              {task.workspace_name && (
                                <span className="text-xs text-muted-foreground">{task.workspace_name}</span>
                              )}
                              {task.due_time && (
                                <span className="text-xs text-blue-500 dark:text-blue-400">at {task.due_time}</span>
                              )}
                              {task.assigned_to_name && (
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                  <User className="w-3 h-3" /> {task.assigned_to_name}
                                </span>
                              )}
                            </div>
                          </div>
                          {task.priority && (
                            <Badge className={`text-[10px] ${
                              task.priority === 'urgent' ? 'priority-badge-urgent' :
                              task.priority === 'high' ? 'priority-badge-high' :
                              task.priority === 'medium' ? 'priority-badge-medium' :
                              'priority-badge-low'
                            }`}>
                              {task.priority}
                            </Badge>
                          )}
                        </div>
                        <SubtaskRows task={task} />
                      </div>
                    ))}
                    {todayTasks.length > 5 && (
                      <Button variant="ghost" className="w-full text-sm" onClick={() => navigate('/tasks')}>
                        View all {todayTasks.length} tasks <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Overdue Tasks */}
            <Card className={`dark:bg-slate-800 dark:border-slate-700 ${overdueTasks.length > 0 ? 'border-red-200 dark:border-red-800' : ''}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-foreground">
                  <AlertTriangle className={`w-5 h-5 ${overdueTasks.length > 0 ? 'text-red-500 dark:text-red-400' : 'text-muted-foreground'}`} />
                  Overdue Tasks
                  {overdueTasks.length > 0 && (
                    <Badge className="ml-2 bg-red-500">{overdueTasks.length}</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {overdueTasks.length === 0 ? (
                  <p className="text-muted-foreground text-sm py-4 text-center">No overdue tasks. Great work!</p>
                ) : (
                  <div className="space-y-2">
                    {overdueTasks.slice(0, 5).map(task => (
                      <div key={task.task_id} className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-950">
                        <div className="flex items-start gap-3">
                          <div className={`w-4 h-4 mt-0.5 rounded-full border-2 flex-shrink-0 ${task.completed ? 'bg-[#4A9CC7] border-[#4A9CC7]' : 'border-red-300 dark:border-red-600'}`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-foreground">{task.title}</p>
                            <div className="flex flex-wrap items-center gap-2 mt-1">
                              <span className="text-xs text-red-500 dark:text-red-400">
                                Due: {formatTaskDate(task.due_date, task.due_time)}
                              </span>
                              {task.assigned_to_name && (
                                <span className="text-xs text-muted-foreground flex items-center gap-1">
                                  <User className="w-3 h-3" /> {task.assigned_to_name}
                                </span>
                              )}
                            </div>
                          </div>
                          {task.priority === 'high' && (
                            <Badge className="text-[10px] priority-badge-high">high</Badge>
                          )}
                        </div>
                        <SubtaskRows task={task} />
                      </div>
                    ))}
                    {overdueTasks.length > 5 && (
                      <Button variant="ghost" className="w-full text-sm text-red-600 dark:text-red-400" onClick={() => navigate('/tasks')}>
                        View all {overdueTasks.length} overdue <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* All Tasks by Workspace */}
          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2 text-foreground">
                <FolderKanban className="w-5 h-5 text-[#4A9CC7]" />
                Pending Tasks by Workspace
              </CardTitle>
            </CardHeader>
            <CardContent>
              {Object.keys(groupedTasks).length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-green-300" />
                  <p className="text-muted-foreground">All caught up! No pending tasks.</p>
                  <Button className="mt-4 bg-[#4A9CC7]" onClick={() => navigate('/tasks')}>
                    Go to Tasks
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.entries(groupedTasks).map(([wsKey, wsData]) => {
                    const totalPending = Object.values(wsData.projects).reduce((sum, p) => sum + p.tasks.length, 0);
                    return (
                      <Collapsible key={wsKey} open={expandedWorkspaces[wsKey]} onOpenChange={() => toggleWorkspace(wsKey)}>
                        <div className="border dark:border-slate-700 rounded-lg overflow-hidden">
                          <CollapsibleTrigger className="w-full">
                            <div className="flex items-center gap-3 p-4 bg-muted hover:bg-muted/80 transition-colors">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                wsData.workspace_type === 'team' ? 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400' :
                                wsData.workspace_type === 'client' ? 'bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400' :
                                'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-400'
                              }`}>
                                {getWorkspaceIcon(wsData.workspace_type)}
                              </div>
                              <div className="flex-1 text-left">
                                <h4 className="font-medium text-foreground">{wsData.workspace_name}</h4>
                                <p className="text-xs text-muted-foreground">
                                  {totalPending} pending task{totalPending !== 1 ? 's' : ''}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge className="bg-[#4A9CC7]/10 text-[#4A9CC7] text-xs">{totalPending}</Badge>
                                {expandedWorkspaces[wsKey] ? (
                                  <ChevronUp className="w-4 h-4 text-muted-foreground" />
                                ) : (
                                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                )}
                              </div>
                            </div>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <div className="p-4 pt-0 space-y-4">
                              {Object.entries(wsData.projects).map(([projKey, projData]) => (
                                <div key={projKey} className="ml-2">
                                  <div className="flex items-center gap-2 mb-2 mt-3">
                                    <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: projData.project_color || '#4A9CC7' }} />
                                    <span className="text-sm font-medium text-foreground">{projData.project_name}</span>
                                    <span className="text-xs text-muted-foreground">({projData.tasks.length} pending)</span>
                                  </div>
                                  <div className="space-y-1 ml-5">
                                    {projData.tasks.slice(0, 5).map(task => (
                                      <div key={task.task_id} className="p-2 rounded hover:bg-muted">
                                        <div className="flex items-start gap-2">
                                          <div className={`w-3.5 h-3.5 mt-0.5 rounded-full border-2 flex-shrink-0 ${task.completed ? 'bg-[#4A9CC7] border-[#4A9CC7]' : 'border-gray-300 dark:border-gray-600'}`} />
                                          <div className="flex-1 min-w-0">
                                            <span className={`text-sm text-foreground ${task.completed ? 'line-through text-muted-foreground' : ''}`}>
                                              {task.title}
                                            </span>
                                            <div className="flex flex-wrap items-center gap-2 mt-0.5">
                                              {task.due_date && (
                                                <span className={`text-xs ${
                                                  isPast(parseISO(task.due_date)) && !task.completed
                                                    ? 'text-red-500 dark:text-red-400'
                                                    : 'text-muted-foreground'
                                                }`}>
                                                  <Calendar className="w-3 h-3 inline mr-1" />
                                                  {formatTaskDate(task.due_date, task.due_time)}
                                                </span>
                                              )}
                                              {task.assigned_to_name && (
                                                <span className="text-xs text-muted-foreground">
                                                  <User className="w-3 h-3 inline mr-1" />
                                                  {task.assigned_to_name}
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                          {task.priority && (
                                            <Badge className={`text-[9px] ${
                                              task.priority === 'urgent' ? 'priority-badge-urgent' :
                                              task.priority === 'high' ? 'priority-badge-high' :
                                              task.priority === 'medium' ? 'priority-badge-medium' :
                                              'priority-badge-low'
                                            }`}>
                                              {task.priority}
                                            </Badge>
                                          )}
                                        </div>
                                        <SubtaskRows task={task} />
                                      </div>
                                    ))}
                                    {projData.tasks.length > 5 && (
                                      <p className="text-xs text-gray-400 ml-6 py-1">
                                        +{projData.tasks.length - 5} more tasks
                                      </p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </CollapsibleContent>
                        </div>
                      </Collapsible>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
};

export default AllTasksDashboard;
